import React, { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClock } from '@fortawesome/pro-light-svg-icons/faClock';
import { TypedMemo } from '../../../common/types';
import { Segmented, SegmentedOption } from '../../components/primitives/Segmented';
import { slotLabel } from '../slotLabel';
import { ComposerTiming } from './useComposerDraft';
import { ComposerPostingModes } from './composerPostingModes';
import { ComposerRemindersNote } from './ComposerRemindersNote';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the whole Promote pillar; each part uses its own slice of it */
import styles from '../Promote.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface ComposerTimingSectionProps {
  timing: ComposerTiming;
  pickedAt: string;
  /** The channel that goes out first, and the hour it goes out at. */
  firstChannel?: string;
  firstSlot?: string;
  /** How many channels follow the first one. */
  othersCount: number;
  /** How the posts of the picked channels go out: by themselves or as reminders (QA finding B11). */
  postingModes?: ComposerPostingModes;
  onChangeTiming: (timing: ComposerTiming) => void;
  onChangePickedAt: (value: string) => void;
}

/** When the post goes out: now, each channel's own hour, or a time you set. */
const ComposerTimingSectionComponent: React.FC<ComposerTimingSectionProps> = ({
  timing,
  pickedAt,
  firstChannel,
  firstSlot,
  othersCount,
  postingModes,
  onChangeTiming,
  onChangePickedAt,
}) => {
  const intl = useIntl();

  const label = intl.formatMessage({ id: 'app.sml.promote.composer.timing', defaultMessage: 'Timing' });

  const options = useMemo<SegmentedOption<ComposerTiming>[]>(
    () => [
      {
        value: 'now',
        label: intl.formatMessage({ id: 'app.sml.promote.composer.timing.now', defaultMessage: 'Publish now' }),
      },
      {
        value: 'best',
        label: intl.formatMessage({ id: 'app.sml.promote.composer.timing.bestAi', defaultMessage: 'Best time' }),
      },
      {
        value: 'pick',
        label: intl.formatMessage({ id: 'app.sml.promote.composer.timing.pick', defaultMessage: 'Pick a time' }),
      },
    ],
    [intl]
  );

  const changePicked = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => onChangePickedAt(event.target.value),
    [onChangePickedAt]
  );

  const hasPlan = firstChannel !== undefined && firstSlot !== undefined;

  return (
    <div className={styles.stack}>
      <span className={styles.sectionLabel}>{label}</span>
      <Segmented options={options} value={timing} onChange={onChangeTiming} ariaLabel={label} />
      {postingModes !== undefined ? <ComposerRemindersNote modes={postingModes} /> : null}
      {timing === 'best' ? (
        <p className={styles.planNote}>
          <FontAwesomeIcon icon={faClock} />
          {hasPlan
            ? intl.formatMessage(
                {
                  id: 'app.sml.promote.composer.timing.stagger',
                  defaultMessage:
                    'Each channel posts at its own peak — {channel} at {slot}{others, plural, =0 {} other {, # more staggered behind it}}.',
                },
                { channel: firstChannel, slot: slotLabel(intl, firstSlot), others: othersCount }
              )
            : intl.formatMessage({
                id: 'app.sml.promote.composer.timing.stagger.empty',
                defaultMessage: 'Pick a channel and its peak hour appears here.',
              })}
        </p>
      ) : null}
      {timing === 'pick' ? (
        <input
          type="datetime-local"
          className={styles.handleInput}
          value={pickedAt}
          aria-label={intl.formatMessage({
            id: 'app.sml.promote.composer.timing.pickLabel',
            defaultMessage: 'Date and time',
          })}
          onChange={changePicked}
        />
      ) : null}
    </div>
  );
};

export const ComposerTimingSection = TypedMemo(ComposerTimingSectionComponent);
